/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Log } from '../../AuxillaryService/Log';
import { ImageCache } from '../../Cache/ImageCache';
import { getZoneById } from '../../Display';
import { ReachAPI } from '../../ReachApi';
import { ApiSuccessResponse } from '../../ReachApi';
import { glom } from '../../Signage/glom';
import { TemplateWidgetMixin, VueTemplate } from '../../Template';
import { WidgetType } from '../types';
import { WidgetWithScheduler } from '../WidgetWithScheduler';
import { LayoutType, PostType, Vendors } from './enums';
import {
    convertToDateOrTime,
    createFacebookPost,
    createInstagramBusinessPost,
    createInstagramPost,
    createSlackPost,
    createTwitterPost,
    createWorkplacePost,
    truncateString,
    round,
    getSiblingNodes,
    wrap,
} from './helpers';
import {
    RawSocialMediaPost,
    SocialMediaPost,
    SocialMediaWidgetConfiguration,
    SocialMediaWidgetConfigurationDetails,
    StackPageObject,
    SocialMedallionTimelineSlot,
    SocialMedallionTimelineSlotParams,
    ZodSocialMediaWidgetConfigurationDetails,
    ZodTwitterPost,
    ZodFacebookPost,
    ZodInstagramPost,
    ZodInstagramBusinessPost,
    ZodWorkplacePost,
    ZodSlackPost,
    SocialMediaRenderContext,
} from './types';
import { isNullish } from '../../Utility/Nullish';
import { getSocialXPosts } from '../../ReachApiV2/endpoints/social';
import './socialmedallion.scss';

export class SocialMedallionWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    type: WidgetType.MEDALLION = WidgetType.MEDALLION;
    declare config: SocialMediaWidgetConfigurationDetails;

    /**
     * Social Media ID
     */
    #socialmediaId: string;

    /**
     * Social Media Vendor (e.g. Facebook, Twitter, Instagram)
     */
    #vendor: string;

    /**
     * Widget Width
     */
    #width: number;

    /**
     * Widget Height
     */
    #height: number;

    /**
     * Template ID
     */
    #templateId: string;

    /**
     * Number of Posts to display
     */
    #numLatestPosts: number;

    /**
     * Timezone of the widget
     */
    #timezone: string;

    /**
     * Keeps track of which post to display
     * postIndex ???
     */
    #postCount: number;

    /**
     * Array container for all post objects
     */
    #postArray: SocialMediaPost[];

    /**
     * Keeps track of which stack page to count
     */
    #stackPageCount: number;

    /**
     * Array container for stacked pages
     */
    #stackPages: StackPageObject[];

    /**
     * Container for post IDs, helps with determining if anything changed
     */
    #postIdsSet: Set<string>;

    /**
     * True if posts change in any way
     */
    #postsChanged;

    /**
     * true if set to only display pictures
     */
    #picsOnly: boolean;

    /**
     * true if set to only display texts
     */
    #textOnly: boolean;

    /**
     * REACH profile id
     */
    #reachId: string;

    /**
     * true if multiple posts should appear at once
     */
    #stacked: boolean;

    /**
     * true if posts have been acquired from API
     */
    #hasPosts: boolean;

    /**
     * For backwards compatibility Vue templates uses this to calculate text overflow conditions
     */
    get height(): number {
        return this.#height;
    }

    constructor(widgetConfiguration: SocialMediaWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.type = WidgetType.MEDALLION;
        const schedulerRunInterval = parseInt(this.config.checkbackinterval);
        this.schedulerRunInterval = isNaN(schedulerRunInterval) ? 60 : schedulerRunInterval;

        const zone = getZoneById(zoneId);

        this.#socialmediaId = this.config.smid;
        this.#vendor = this.config.vendor;

        this.#width = zone.width;
        this.#height = zone.height;

        this.#templateId = this.config.template;

        this.#numLatestPosts = this.config.postnum ? parseInt(this.config.postnum) : 5;
        this.#timezone = this.config.timezone;
        this.#postCount = 0;
        this.#postArray = [];
        this.#stackPageCount = 0;
        this.#stackPages = [];
        this.#postIdsSet = new Set();
        this.#postsChanged = false;
        this.#picsOnly = this.config.images_only;
        this.#textOnly = this.config.text_only;
        this.#reachId = this.config.reach_profile_id;
        this.#stacked = this.config.stacking;
        this.zoneId = zoneId;
        this.#hasPosts = false;
        if (this.#templateId.length < 1 || this.#reachId.length < 1) {
            throw new Error(
                `Social Medallion Widget ${this.id} has no template or reach profile id`
            );
        }
    }

    protected validateConfigurationDetails(
        socialMediaWidgetConfigurationDetails: SocialMediaWidgetConfigurationDetails
    ): SocialMediaWidgetConfigurationDetails {
        return ZodSocialMediaWidgetConfigurationDetails.parse(
            socialMediaWidgetConfigurationDetails
        );
    }

    schedulerAction(): void {
        this.scheduledRenderPosts();
    }

    async initialize(): Promise<void> {
        this.hasContentToPlay = true;
        await super.initialize();
        await super.startScheduler();
    }

    /**
     * Renders the template and calls showSlot so the content reloads
     */
    async getReadyForDisplay(currentSlot: SocialMedallionTimelineSlot): Promise<void> {
        if (currentSlot) {
            await this.renderTemplate();
            this.showSlot(currentSlot);
        }
    }

    showSlot(currentSlot: SocialMedallionTimelineSlot): void {
        const element = this.getSlotElement(currentSlot);
        if (element) {
            element.classList.add('social-media-show-container');
            element.classList.remove('social-media-hide-container');

            getSiblingNodes(element).forEach((sibling) => {
                sibling.classList.add('social-media-hide-container');
                sibling.classList.remove('social-media-show-container');
            });
        }
    }

    hideSlot(currentSlot: SocialMedallionTimelineSlot): void {
        const element = this.getSlotElement(currentSlot);
        if (element) {
            element.classList.add('social-media-hide-container');
            element.classList.remove('social-media-show-container');
        }
    }

    /**
     * Get the HTMLElement for the slot
     */
    getSlotElement(currentSlot: SocialMedallionTimelineSlot): HTMLElement | null {
        let element: HTMLElement | null;
        if (this.#stacked) {
            element = this.wrapperElement.querySelector(
                `.stacked-container-${currentSlot.params.thisloopcount}`
            );
        } else {
            element = currentSlot?.params?.post?.postElement || null;

            /**
             * This is a fix for a race condition.  It is caused by a possibly stale reference
             * to the post element.  If a template is re-rendered after the slots have been
             * created, the referenced element will no longer be in the dom.  A new, identical
             * element will be re-rendered.  So we can use the id of the original to find the
             * freshly rendered post element.  Sorry for the band-aid solution :(
             */
            if (element !== null && !document.contains(element)) {
                element = document.getElementById(element.id);
            }
        }

        return element;
    }

    async beforeFadeIn(currentSlot: SocialMedallionTimelineSlot): Promise<{ abortLoop: boolean }> {
        if (this.#postsChanged) {
            // Set posts changed to false here because reconstruction has already finished
            this.#postsChanged = false;

            await this.renderTemplate();

            this.showSlot(currentSlot);

            return {
                abortLoop: true,
            };
        } else {
            this.showSlot(currentSlot);
            return {
                abortLoop: false,
            };
        }
    }

    async beforeFadeOut(
        currentSlot: SocialMedallionTimelineSlot
    ): Promise<{ cancelFade: boolean }> {
        return {
            cancelFade: currentSlot.params.totalposts === 1,
        };
    }

    async afterFadeOut(currentSlot: SocialMedallionTimelineSlot): Promise<void> {
        this.hideSlot(currentSlot);
    }

    /**
     * Determines slots to send to timeline
     * @param numSlots - maximum number of slots
     * @param maxDuration - time left in loop
     */
    querySlots(numSlots: number, maxDuration: number): SocialMedallionTimelineSlot[] {
        this.hasContentToPlay = this.#postArray.length === 0 ? false : true;

        if (this.#postArray.length === 0) {
            return []; //defaulting to this as error
        }

        const slots = [];
        let params: SocialMedallionTimelineSlotParams;
        for (let i = 0; i < numSlots; ++i) {
            if (this.duration <= maxDuration) {
                if (this.#stacked) {
                    params = {
                        thisloopcount: this.#stackPageCount,
                        stackPage: this.#stackPages[this.#stackPageCount],
                    };

                    this.#stackPageCount++;
                    if (this.#stackPageCount > this.#stackPages.length - 1) {
                        this.#stackPageCount = 0;
                    }
                } else {
                    params = {
                        thisloopcount: this.#postCount,
                        post: this.#postArray[this.#postCount],
                    };

                    this.#postCount++;
                    if (this.#postCount > this.#postArray.length - 1) {
                        this.#postCount = 0;
                    }
                }

                const slot = this.generateSlot(this.duration, undefined, params);
                slots.push(slot);

                maxDuration -= this.duration;
            } else {
                break;
            }
        }

        return slots;
    }

    querySlotsLinear(timeBased?: boolean): SocialMedallionTimelineSlot[] {
        const slots: SocialMedallionTimelineSlot[] = [];

        this.hasContentToPlay = this.#postArray.length === 0 ? false : true;

        if (this.#postArray.length === 0) {
            return slots; //defaulting to this as error
        }

        if (timeBased) {
            let usedTime = 0;

            do {
                const slot = this.generateSlot(this.duration, undefined, {
                    thisloopcount: this.#postCount,
                    post: this.#postArray[this.contentStartIndex],
                });
                slots.push(slot);

                //if the next item is undefined, set contentStartIndex to 0
                this.contentStartIndex =
                    this.#postArray[this.contentStartIndex + 1] !== undefined
                        ? this.contentStartIndex + 1
                        : 0;
                usedTime += this.duration;
            } while (usedTime < this.minimumLoopTime);
        } else {
            for (let i = 0; i < this.#postArray.length; ++i) {
                this.#postCount++;

                const slot = this.generateSlot(this.duration, undefined, {
                    thisloopcount: i,
                    post: this.#postArray[i],
                });
                slots.push(slot);
            }
        }

        return slots;
    }

    /**
     * Convert raw social media posts into format usable in templates
     */
    constructPosts<T extends RawSocialMediaPost>(rawPosts: T[]): SocialMediaPost[] {
        switch (this.#vendor) {
            case Vendors.Facebook:
                return rawPosts.map((rawPost) =>
                    createFacebookPost(
                        ZodFacebookPost.parse(rawPost),
                        this.#socialmediaId,
                        this.#reachId,
                        this.#templateId
                    )
                );
            case Vendors.Twitter:
                return rawPosts.map((rawPost) =>
                    createTwitterPost(ZodTwitterPost.parse(rawPost), this.#templateId)
                );
            case Vendors.Instagram:
                return rawPosts.map((rawPost) =>
                    createInstagramPost(ZodInstagramPost.parse(rawPost), this.#templateId)
                );
            case Vendors.InstagramBusiness:
                return rawPosts.map((rawPost) =>
                    createInstagramBusinessPost(
                        ZodInstagramBusinessPost.parse(rawPost),
                        this.#templateId
                    )
                );
            case Vendors.Workplace:
                return rawPosts.map((rawPost) =>
                    createWorkplacePost(ZodWorkplacePost.parse(rawPost), this.#templateId)
                );
            case Vendors.Slack:
                return rawPosts.map((rawPost) =>
                    createSlackPost(ZodSlackPost.parse(rawPost), this.#templateId)
                );
            default:
                throw new Error('Vendor not supported');
        }
    }

    /**
     * Make API call to get Social Media posts
     */
    async loadApiData<T extends RawSocialMediaPost>(): Promise<T[]> {
        if (!glom.playerUuid && !glom.securityToken) {
            throw new Error('Player uuid or security token must be set');
        }
        const security = new ReachAPI.Security(
            glom.playerUuid || undefined,
            glom.securityToken || undefined
        );

        const numOfLatestPosts = this.#picsOnly ? 25 : this.#numLatestPosts;

        try {
            const response: ApiSuccessResponse & { posts: T[] } =
                await ReachAPI.socialmedia.getPosts(
                    this.#vendor,
                    numOfLatestPosts,
                    security,
                    this.#socialmediaId,
                    this.#reachId,
                    this.#timezone
                );

            return response.posts;
        } catch (error) {
            Log.Warning(
                `Failed to get the posts for the Social Media Widget: ${this.id}`,
                null,
                __fileline,
                error
            );
            throw error;
        }
    }

    /**
     * //renderPosts
     * Method called by schedulerAction to add any new posts and remove old posts
     */
    async scheduledRenderPosts(): Promise<void> {
        let posts = [];
        if (this.#vendor === Vendors.Twitter) {
            posts = [
                ...(await getSocialXPosts({
                    profileId: Number(this.#reachId),
                    numberOfPosts: this.#numLatestPosts,
                })),
            ];
        } else {
            posts = await this.loadApiData();
        }

        let resultPosts = this.constructPosts(posts);

        // Get posts to display equal to numLatestPosts
        // If the mode is pics only, get the posts of type photo equal to numLatestPosts

        if (this.#picsOnly) {
            const picturePosts = resultPosts.filter((post) => {
                return (
                    post.posttype === PostType.Photo ||
                    post.posttype === PostType.Image ||
                    post.posttype === PostType.Carousel
                );
            });
            resultPosts = picturePosts.slice(0, this.#numLatestPosts);
        }

        if (this.#textOnly) {
            const textPosts = resultPosts.filter((post) => {
                return post.posttype === PostType.Text;
            });
            resultPosts = textPosts.slice(0, this.#numLatestPosts);
        }
        // Set hasPosts to true since thing might be initial load
        if (!this.#hasPosts && resultPosts.length > 0) {
            this.#hasPosts = true;
            this.#postArray.push(...resultPosts);
            this.#postArray.forEach((post) => this.#postIdsSet.add(post.id));
            await this.renderTemplate();
        } else {
            // these ids is used to determine if a post should be added or removed
            const uniqueIdsOfPosts = resultPosts.map((post) => post.id);

            const postsToAdd = uniqueIdsOfPosts.filter((id) => !this.#postIdsSet.has(id));

            const postsToRemove = Array.from(this.#postIdsSet).filter(
                (id) => !uniqueIdsOfPosts.includes(id)
            );

            /* now add posts and re-organize postArray
             * after initial get of posts set postsChanged to update DOM at
             * strategic moment beforeFadeIn
             */
            if (postsToAdd.length > 0 || postsToRemove.length > 0) {
                this.#postsChanged = true;

                // empty the postArray and push new contents
                this.#postArray.length = 0;
                this.#postArray.push(...resultPosts);
                this.#postIdsSet.clear();
                this.#postArray.forEach((post) => this.#postIdsSet.add(post.id));

                await this.renderTemplate();
            }
        }

        this.#postArray.forEach((post) => {
            /**
             * post.postElement is already set for vue socialmedia templates
             * check this so that the value is not overwritten
             */
            if (!post.postElement) {
                const selector = `#social-media-wall-post-container-${this.id}-${post.id}`;
                post.postElement = this.wrapperElement.querySelector<HTMLDivElement>(selector);
            }
        });
    }

    /**
     * Apply side header and background color (html)
     *
     */
    applySideHeaderAndBackgroundColor(
        postElement: HTMLDivElement,
        uniqueDivId: string,
        post: SocialMediaPost
    ): void {
        const backgroundColor = post.color;
        const sideHeader: HTMLDivElement | null = postElement.querySelector(
            `#side-header-${uniqueDivId}`
        );

        if (sideHeader) {
            ImageCache.getCached('assets/' + post.sm + '-side-header.png')
                .done((url: string) => {
                    sideHeader.style.backgroundImage = `url('${url}')`;
                })
                .fail((...args: [XMLHttpRequest, string, string, Event]) => {
                    Log.Warning(
                        'Failed to get ' + post.sm + ' medallion side header image.',
                        null,
                        __fileline,
                        ImageCache.createPromiseFailObject.apply(this, args)
                    );
                });
            sideHeader.style.backgroundColor = backgroundColor;
        }
    }

    /**
     * Apply profile picture (html)
     */
    applyProfilePic(postElement: HTMLDivElement, uniqueDivId: string, post: SocialMediaPost): void {
        const profilePic: HTMLDivElement | null = postElement.querySelector(
            `#social-media-prof-pic-${uniqueDivId}`
        );

        if (profilePic) {
            profilePic.innerHTML = `<img src='${post.profilepic}' width='60' height='auto' alt='Profile Picture' />`;
        }
    }

    /**
     * Set image for the post (html) (older templates)
     * @param postElement - wrapper div for post
     * @param messageWithoutLinks - post message with links removed
     * @param postMessage - post message div
     * @param post
     * @param uniqueDivId
     * @param updatedTemplate - Wrapper for the new template
     */
    setBodyImage(
        postElement: HTMLDivElement,
        messageWithoutLinks: string | null,
        postMessage: HTMLDivElement | null | undefined,
        post: SocialMediaPost,
        uniqueDivId: string,
        updatedTemplate: HTMLDivElement | null | undefined
    ): number {
        let bodyImgHeight;
        let imgHeight;
        const logo = postElement.querySelector<HTMLElement>(`#social-media-logo-${this.id}`);

        const logoHeight = logo ? logo.offsetHeight + 30 : 0;

        const header = postElement.querySelector<HTMLElement>(
            `#social-media-body-header-${uniqueDivId}`
        );

        const headerHeight = header ? header.offsetHeight : 0;

        const bodyImage: HTMLElement | null = postElement.querySelector(
            `#social-media-body-img-${uniqueDivId}`
        );

        const dataImageHeightRatio = bodyImage?.getAttribute('data-img-ratio');

        const isFullSized = bodyImage?.classList.contains('full-sized') || false;

        const imgHeightRatio = isFullSized ? 1 : Number(dataImageHeightRatio) || 0.44;

        if (messageWithoutLinks !== null && messageWithoutLinks !== '') {
            bodyImgHeight = 0;

            if (isFullSized) {
                bodyImgHeight = headerHeight + 20;
            }

            imgHeight = Math.round(this.#height * imgHeightRatio) - bodyImgHeight;
        } else {
            imgHeight = this.#height - (logoHeight + headerHeight + 20);
        }

        const dontResizeBodyImage = bodyImage?.classList.contains('dont-resize') || false;

        if (!dontResizeBodyImage && bodyImage) {
            bodyImage.style.height = `${imgHeight}px`;
        }

        this.setXtraMedia(bodyImage, imgHeight, post); //set instagram video

        // set the height of the body message
        const remainingHeight = this.#height - headerHeight - logoHeight - imgHeight - 20;

        const dontResizePostImage = postMessage?.classList.contains('dont-resize') || false;
        if (postMessage) {
            if (!dontResizePostImage) {
                postMessage.style.height =
                    String(
                        post.posttype != 'text' && post.image !== null
                            ? remainingHeight
                            : this.#height - (logoHeight + headerHeight + 20)
                    ) + 'px';
            }

            if (messageWithoutLinks === null || messageWithoutLinks === '') {
                postMessage.style.display = 'none';
                if (updatedTemplate) {
                    //set default font size
                    const fontSize = this.getFontSize(postElement);
                    postElement.style.fontSize = `${fontSize}px`;
                }
            } else {
                postMessage.style.display = 'block';
            }
        }
        return imgHeight;
    }

    /**
     * @deprecated
     * @todo: should be removed - xtramedia is always null and
     * we do not have video support at the moment
     */
    setXtraMedia(bodyImage: HTMLElement | null, imgHeight: number, post: SocialMediaPost): void {
        if (post.xtramedia !== null && bodyImage) {
            bodyImage.innerHTML = `
                <video
                    id="post_img_${this.id}-${post.id}"
                    height="${imgHeight}"
                    loop
                    preload="auto"
                    poster="images/content_loading.gif"
                >
                    <source src="${post.xtramedia}" type="video/mp4">
                    Your browser does not support HTML5 video.
                </video>`;

            if (post.posttype === 'video' || post.posttype !== 'photo') {
                bodyImage.classList.add('embed-responsive');
            }
        }
    }

    /**
     * Calculates an appropriate font size based on layout type
     */
    getFontSize(postElement: HTMLDivElement): number {
        // the equation of the trend line y = 54x -209, where, y is screen width and
        // x is font-size
        if (this.isPortrait()) {
            return Math.round(
                ((postElement.clientWidth + 209) / 54) * (this.#height / this.#width)
            );
        }

        return Math.round((postElement.clientWidth + 209) / 54);
    }

    /**
     * Find out if layout is portrait or landscape
     */
    getLayoutType(): string {
        if (this.#height > this.#width) {
            return LayoutType.Portrait;
        }

        return LayoutType.Landscape;
    }

    /**
     * @todo set as private
     */
    isLandscape(): boolean {
        return this.getLayoutType() === LayoutType.Landscape;
    }

    /**
     * @todo set as private
     */
    isPortrait(): boolean {
        return this.getLayoutType() === LayoutType.Portrait;
    }

    /**
     * Set image CSS height and width
     */
    setImageHeightWidth(wrapper: HTMLDivElement, image: HTMLImageElement): void {
        const maxHeight =
            0.8 * parseFloat(getComputedStyle(wrapper, null).height.replace('px', ''));
        const maxWidth = 0.9 * parseFloat(getComputedStyle(wrapper, null).width.replace('px', ''));
        const initialWidth = parseFloat(getComputedStyle(image, null).width.replace('px', ''));
        let imageWidth = initialWidth;
        let imageHeight = parseFloat(getComputedStyle(image, null).height.replace('px', ''));
        const ratio = imageWidth / imageHeight; // max-height or max-width is set in CSS these values do not change

        if (imageHeight > maxHeight || imageWidth > maxWidth) {
            image.style.height = 'auto';
            image.style.width = 'auto';
        } else {
            while (imageHeight < maxHeight && imageWidth < maxWidth) {
                imageWidth += 2;
                imageHeight += 2 * ratio;
                image.style.width = `${imageWidth}px`;

                if (imageWidth === initialWidth) {
                    //if true, it probably means width is set to !important
                    break;
                }
            }
        }
    }

    /**
     * Set image for the post (html) (newer templates)
     *
     * @param _postElement - wrapper div for post
     * @param bodyImage - wrapper div for image older templates
     * @param postMedia - wrapper div for image newer templates
     * @param post
     * @param imgHeight
     */
    loadAndSetImageVisibility(
        _postElement: HTMLDivElement,
        bodyImage: HTMLDivElement | null | undefined,
        postMedia: HTMLDivElement | null | undefined,
        post: SocialMediaPost,
        imgHeight: number
    ): void {
        // hide post image and body if they have no content
        if (post.image === null) {
            if (bodyImage) {
                bodyImage.style.display = 'none';
            }
            if (postMedia) {
                postMedia.style.display = 'none';
            }
        } else {
            if (bodyImage) {
                bodyImage.style.display = 'block';
            }
            if (postMedia) {
                postMedia.style.display = 'block';
            }
        }

        // for posts with images
        if (post.posttype !== PostType.Text && post.image !== null && post.xtramedia === null) {
            //if template version 2
            if (postMedia) {
                const mediaClass = this.isPortrait() ? 'post-image-vertical' : 'post-image';
                const postMediaImage = document.createElement('img');
                postMediaImage.id = `post_img${this.id}-${post.id}`;
                if (post.image) {
                    postMediaImage.src = post.image;
                }
                postMediaImage.classList.add(mediaClass);

                postMedia.appendChild(postMediaImage);

                postMediaImage.onload = () => {
                    postMediaImage.classList.add('auto-width');
                    this.setImageHeightWidth(postMedia, postMediaImage);
                };
            } else {
                const postImage = document.createElement('img');
                postImage.id = `post_img${this.id}-${post.id}`;
                if (post.image) {
                    postImage.src = post.image;
                }
                if (bodyImage) {
                    bodyImage.appendChild(postImage);
                    const maxImageHeight = imgHeight;
                    const maxImageWidth = this.#width - 60;

                    postImage.onload = () => {
                        const picWidth = this.#width;
                        const picHeight = this.#height;

                        const widthRatio = round(maxImageWidth / picWidth, 2);
                        const heightRatio = round(maxImageHeight / picHeight, 2);

                        const resizeRatio = widthRatio < heightRatio ? widthRatio : heightRatio;

                        const newHeight = Math.round(resizeRatio * picHeight);

                        if (!bodyImage.classList.contains('dont-resize')) {
                            postImage.setAttribute('height', `${newHeight}px`);
                            bodyImage.style.top = `${Math.round(
                                (maxImageHeight - newHeight) / 2
                            )}px`;
                        } else {
                            postImage.setAttribute('height', '100%');
                            postImage.setAttribute('width', 'auto');
                        }
                    };
                }
            }
        }
    }

    /**
     * Render date time (html)
     */
    renderDateTime(postElement: HTMLDivElement, uniqueDivId: string, postDate: Date): void {
        // render date and time
        if (postElement) {
            const updated = postElement.querySelector(`#social-media-updated-text-${uniqueDivId}`);
            if (updated) {
                updated.innerHTML = 'Updated:<br />';
            }

            const date = postElement.querySelector(`#social-media-date-${uniqueDivId}`);
            if (date) {
                date.innerHTML = convertToDateOrTime(postDate, 'date');
            }

            const time = postElement.querySelector(`#social-media-time-${uniqueDivId}`);
            if (time) {
                time.innerHTML = convertToDateOrTime(postDate, 'time');
            }
        }
    }

    /**
     * Set text for the post and adjust font size
     */
    setPostMessage(
        updatedTemplate: HTMLDivElement,
        postElement: HTMLDivElement,
        messageWithoutLinks: string | undefined,
        _maxChars: number,
        uniqueDivId: string
    ): void {
        const padding = 10;
        const minimumFontSize = 16;
        let postMessageWrapper =
            postElement.querySelector<HTMLDivElement>('.post-text-only-wrapper');
        const textOnlyPost = updatedTemplate.querySelector<HTMLDivElement>(
            `#social-media-message-${uniqueDivId}`
        );
        const hashtagDiv = postElement.querySelector<HTMLDivElement>('#hashtags');
        const postPicDiv = postElement.querySelector<HTMLDivElement>(
            `#social-media-post-pic-msg-${uniqueDivId}`
        );
        let msgDiv: HTMLDivElement | null | undefined;
        let optimumFontSize: number;
        let overflowElement: HTMLElement | null | undefined;

        if (this.#textOnly) {
            msgDiv = textOnlyPost?.querySelector<HTMLDivElement>('div.text-div');

            if (!textOnlyPost || !msgDiv) {
                Log.Warning(
                    `Could not find HTMLElement: div.text-div OR #social-media-message-*`,
                    null,
                    __fileline
                );
                return;
            }
            msgDiv.innerHTML = messageWithoutLinks || '';
            const fontSize = parseInt(msgDiv.style.fontSize) || 0;
            if (fontSize < minimumFontSize) {
                msgDiv.style.fontSize = `${minimumFontSize}px`;
                optimumFontSize = this.calculateOptimumFontSize(updatedTemplate) + 10;
            } else {
                optimumFontSize = parseInt(msgDiv.style.fontSize) || 0;
            }

            if (this.isPortrait()) {
                overflowElement = postMessageWrapper?.parentElement;
            } else {
                overflowElement = postElement.querySelector<HTMLDivElement>(
                    '.socialmedia-content-wrapper'
                );
            }

            if (!overflowElement) {
                Log.Warning(
                    `HTMLElement not found: .socialmedia-content-wrapper`,
                    null,
                    __fileline
                );
                return;
            }

            /*
             * Trying to fix overflow immediately after the content is loaded does not
             * seem to calculate the overflow correctly.
             * Adding a delay seems to fix the issue and the overflow is calculated correctly
             * I am not sure exactly why but my guess is either the rendering of the divs happen in
             * a deferred manner or the right font is not loaded when the calculation happens.
             * Hence, adding a delay so that font has time to download/load.
             */
            setTimeout(() => {
                this.fixTextOnlyContentOverflowing(
                    messageWithoutLinks,
                    optimumFontSize,
                    minimumFontSize,
                    padding,
                    overflowElement,
                    msgDiv,
                    textOnlyPost,
                    hashtagDiv,
                    postMessageWrapper
                );
            }, 5000);
        } else if (postPicDiv) {
            //the layout has image and text
            const postMessageWrapperClass = this.isPortrait()
                ? '.post-text-wrapper-vertical'
                : '.post-text-wrapper';
            postMessageWrapper = postElement.querySelector(postMessageWrapperClass);

            if (this.isPortrait()) {
                postPicDiv.style.height = `${postPicDiv.parentElement?.offsetHeight}px`; //set Height inline which will be boundary for its content
                overflowElement = postPicDiv;
                const contentWrapper: HTMLElement | null = postElement.querySelector(
                    '.socialmedia-content-wrapper'
                );
                if (contentWrapper) {
                    contentWrapper.style.height = '86%'; //('height', '86%');
                }
            } else {
                postPicDiv.style.height = `${postPicDiv.parentElement?.offsetHeight}px`; //set Height inline
                overflowElement = postPicDiv;
            }

            msgDiv = postPicDiv.querySelector<HTMLDivElement>('div.text-div');
            if (!msgDiv) {
                Log.Warning(`Could not find HTMLElement: div.text-div`, null, __fileline);
                return;
            }
            msgDiv.innerHTML = messageWithoutLinks || '';

            //if font-size is not set then calculate it
            if (!parseInt(msgDiv.style.fontSize)) {
                msgDiv.style.fontSize = `${minimumFontSize}px`; //give it a minimum font-size so that the text is not too small
                optimumFontSize = this.calculateOptimumFontSize(updatedTemplate);
            } else {
                optimumFontSize = parseInt(msgDiv.style.fontSize) || 0;
            }

            /*
             * Trying to fix overflow immediately after the content is loaded does not
             * seem to calculate the overflow correctly.
             * Adding a delay seems to fix the issue and the overflow is calculated correctly
             * I am not sure exactly why but my guess is either the rendering of the divs happen in
             * a deferred manner or the right font is not loaded when the calculation happens.
             * Hence, adding a delay so that font has time to download/load.
             */
            setTimeout(() => {
                this.fixContentOverflow(
                    messageWithoutLinks,
                    optimumFontSize,
                    minimumFontSize,
                    padding,
                    overflowElement,
                    msgDiv,
                    postPicDiv,
                    hashtagDiv,
                    postMessageWrapper
                );
            }, 5000);
        }
    }

    /**
     * Fix Content Overflow if the post has image and text
     */
    fixContentOverflow(
        message: string | null | undefined,
        optimumFontSize: number,
        minimumFontSize: number,
        padding: number,
        overflowElement: HTMLElement | null | undefined,
        msgDiv: HTMLDivElement | null | undefined,
        postPicDiv: HTMLDivElement | null | undefined,
        hashtagDiv: HTMLDivElement | null | undefined,
        postMessageWrapper: HTMLDivElement | null | undefined
    ): void {
        if (!overflowElement || !msgDiv || !postPicDiv || !hashtagDiv || !postMessageWrapper) {
            Log.Warning(`HTML Elements should be defined`, null, __fileline);
            return;
        }
        let messageWithoutLinks = message;
        const isTextOverflowing = this.isContentOverflowing(overflowElement);

        while (this.isContentOverflowing(overflowElement)) {
            const lastIndex = messageWithoutLinks?.lastIndexOf(' ');
            messageWithoutLinks = messageWithoutLinks?.substring(0, lastIndex);
            msgDiv.innerHTML = messageWithoutLinks || '';
        }

        //if text is truncated add ellipsis
        if (isTextOverflowing) {
            msgDiv.classList.add('ellipsis');
        }
        let messageFontSize = parseInt(msgDiv.style.fontSize) || 0;

        //if the text is too long the content height might already be more than
        //boundary - so check for it
        if (msgDiv.clientHeight + padding > postPicDiv.clientHeight) {
            msgDiv.style.height = `${postPicDiv.clientHeight - padding}px`; //Make content div smaller than boundary to prevent text cut off
        }

        const initialFontSize = parseInt(msgDiv.style.fontSize) || 0; //To check if font-size is set to !important
        if (!hashtagDiv || !postMessageWrapper) {
            Log.Warning(
                `Could not find HTMLElement: #hashtags OR .post-text-only-wrapper`,
                null,
                __fileline
            );
            return;
        }

        // Prevent accidental infinite while loop
        let loopCount = 0;
        while (
            msgDiv.clientHeight + padding < postPicDiv.clientHeight &&
            msgDiv.clientHeight + hashtagDiv.clientHeight + padding <
                postMessageWrapper.clientHeight
        ) {
            if (++messageFontSize >= optimumFontSize) {
                postPicDiv.style.height = `${msgDiv.clientHeight}px`;
                break;
            }
            loopCount++;
            msgDiv.style.fontSize = `${messageFontSize}px`;
            if (parseInt(msgDiv.style.fontSize) === initialFontSize) {
                //This probably means font-size is set to !important
                break;
            }
            if (loopCount > 50) {
                break;
            }
        }

        //Check if our condition is still valid
        while (
            msgDiv.clientHeight + padding > postPicDiv.clientHeight ||
            msgDiv.clientHeight + hashtagDiv.clientHeight + padding >
                postMessageWrapper.clientHeight
        ) {
            msgDiv.style.fontSize = `${--messageFontSize}px`;
            //Prevent font-size that is too small
            if (messageFontSize < minimumFontSize) {
                break;
            }
        }

        //check if there is enough space for hashtags - if not hide it
        if (postPicDiv.clientHeight + hashtagDiv.clientHeight > postMessageWrapper.clientHeight) {
            hashtagDiv.style.display = 'none';
        }
    }

    /**
     * Fix text overflow if the post is text only
     */
    fixTextOnlyContentOverflowing(
        message: string | null | undefined,
        optimumFontSize: number,
        minimumFontSize: number,
        padding: number,
        overflowElement: HTMLElement | null | undefined,
        msgDiv: HTMLDivElement | null | undefined,
        textOnlyPost: HTMLDivElement | null | undefined,
        hashtagDiv: HTMLDivElement | null | undefined,
        postMessageWrapper: HTMLDivElement | null | undefined
    ): void {
        if (!overflowElement || !msgDiv || !textOnlyPost || !hashtagDiv || !postMessageWrapper) {
            Log.Warning(`HTML Elements should be defined`, null, __fileline);
            return;
        }
        let messageWithoutLinks = message;
        const isTextOverflowing = this.isContentOverflowing(overflowElement);

        while (this.isContentOverflowing(overflowElement)) {
            const lastIndex = messageWithoutLinks?.lastIndexOf(' ');
            messageWithoutLinks = messageWithoutLinks?.substring(0, lastIndex);
            msgDiv.innerHTML = messageWithoutLinks || '';
        }

        //if text is truncated add ellipsis
        if (isTextOverflowing) {
            msgDiv.classList.add('ellipsis');
        }

        //Fix postPicDiv height
        const textOnlyPostHeight = textOnlyPost.clientHeight; // make it inline

        let messageFontSize = parseInt(msgDiv.style.fontSize) || 0;

        //if the text is too long the msgDiv might already be bigger than
        //textDiv - so check for it

        if (msgDiv.clientHeight + padding > textOnlyPost.clientHeight) {
            msgDiv.style.height = `${textOnlyPost.clientHeight - padding}px`;
        }

        const initialFontSize = parseInt(msgDiv.style.fontSize) || 0; //To check if font-size is set to !important

        if (!hashtagDiv || !postMessageWrapper) {
            Log.Warning(
                `Could not find HTMLElement. #hashtags OR .post-text-only-wrapper`,
                null,
                __fileline
            );
            return;
        }

        // To prevent accidental infinite loop
        let loopCount = 0;
        while (
            msgDiv.clientHeight + padding < textOnlyPostHeight &&
            msgDiv.clientHeight + hashtagDiv.clientHeight + padding <
                postMessageWrapper.clientHeight
        ) {
            if (++messageFontSize >= optimumFontSize) {
                textOnlyPost.style.height = `${msgDiv.clientHeight}px`;
                break;
            }
            loopCount++;
            msgDiv.style.fontSize = `${messageFontSize}px`;
            if (parseInt(msgDiv.style.fontSize) === initialFontSize) {
                //This probably means font-size is set to !important
                break;
            }
            if (loopCount > 50) {
                break;
            }
        }

        //Check if our condition is still valid
        loopCount = 0;
        while (
            msgDiv.clientHeight + padding > textOnlyPostHeight ||
            msgDiv.clientHeight + hashtagDiv.clientHeight + padding >
                postMessageWrapper.clientHeight
        ) {
            msgDiv.style.fontSize = `${--messageFontSize}px`;
            loopCount++;
            //Prevent font-size that is too small
            if (messageFontSize < minimumFontSize) {
                break;
            }
            if (loopCount > 50) {
                break;
            }
        }

        //check if there is enough space for hashtags - if not hide it
        if (textOnlyPostHeight + hashtagDiv.clientHeight > postMessageWrapper.clientHeight) {
            hashtagDiv.style.display = 'none';
        }
    }

    /**
     * Check if the content in the HTMLElement is overflowing
     */
    isContentOverflowing(element: HTMLElement): boolean {
        const curOverflow = element.style.overflow;

        element.style.overflow = 'hidden';
        const clientWidth = element.clientWidth;
        const clientHeight = element.clientHeight;

        //Internet Explorer does not calculate scrollWidth properly unless overflow
        //is set to "visible"
        element.style.overflow = 'visible';
        const scrollWidth = element.scrollWidth;
        const scrollHeight = element.scrollHeight;

        const isOverflowing = clientWidth < scrollWidth || clientHeight < scrollHeight;

        element.style.overflow = curOverflow;

        return isOverflowing;
    }

    /**
     * Calculate the best possible font size based on Div height and width
     */
    calculateOptimumFontSize(postElement: HTMLDivElement): number {
        if (this.isPortrait()) {
            return Math.round(
                ((postElement.clientWidth - 24) / 26 + 0.00001) * (this.#height / this.#width)
            );
        }
        return Math.round((postElement.clientWidth - 24) / 26 + 0.00001);
    }

    /**
     * Adjust height of content wrapper
     */
    applyPostWrapperHeight(postElement: HTMLDivElement): void {
        const heightThreshold = 325;
        const optimumHeight = 75;

        const postWrapper = postElement.querySelector<HTMLDivElement>(
            '.socialmedia-content-wrapper'
        );
        if (!postWrapper) {
            Log.Warning(`Could not find HTMLElement.`, null, __fileline);
            return;
        }
        if (postElement.clientHeight < heightThreshold) {
            postWrapper.style.height = `${optimumHeight}%`;
        }
    }

    /**
     * Create socialmedia post, only used by older templates and will be deprecated
     * @param post - post item
     * @param index - post index
     * @param totalPosts
     */
    loadPost(post: SocialMediaPost, index: number, totalPosts: number): void {
        const uniqueId = `${this.id}-${post.id}`; //Appended to widget divs to make it unique
        const postElement = this.wrapperElement.querySelector<HTMLDivElement>(
            `#social-media-wall-post-${uniqueId}`
        );
        const updatedTemplate = postElement?.querySelector<HTMLDivElement>(
            `#sm_template_version_2-${uniqueId}`
        );
        // this can be null - only found in older templates
        const postMessage = postElement?.querySelector<HTMLDivElement>(
            `#social-media-post-msg-${uniqueId}`
        );
        const attr = postMessage?.getAttribute('data-max-chars'); // find maximum characters
        let maxChars = typeof attr !== typeof undefined && attr ? parseInt(attr) : 500;
        const postDate = new Date(Date.parse(post.date.replace(/-/g, '/'))); //the date post was created, the timezone is already converted to the current timezone
        let messageWithoutLinks: string | null | undefined =
            post.message !== null //post message with links stripped
                ? post.message.replace(/(?:https?|ftp):\/\/[\n\S]+/g, '').trim() || null //setting to null rather than empty string so that the post is perceived as image only
                : null;

        const bodyImage = postElement?.querySelector<HTMLDivElement>(
            `#social-media-body-img-${uniqueId}`
        ); //for older templates - wrapper for image
        const postMedia = postElement?.querySelector<HTMLDivElement>(
            `#social-media-post-img-${uniqueId}`
        ); //for template version 2 - wrapper for image

        if (!postElement) {
            Log.Warning(
                `Could not find HTMLElement to load post. Newer templates do not require this step.`,
                null,
                __fileline
            );
            return;
        }

        this.applyPostWrapperHeight(postElement);
        this.applySideHeaderAndBackgroundColor(postElement, uniqueId, post);
        this.applyProfilePic(postElement, uniqueId, post);
        this.renderDateTime(postElement, uniqueId, postDate);

        //max image height - older template
        const imgHeight = this.setBodyImage(
            postElement,
            messageWithoutLinks,
            postMessage,
            post,
            uniqueId,
            updatedTemplate
        );
        this.loadAndSetImageVisibility(postElement, bodyImage, postMedia, post, imgHeight);

        maxChars = typeof attr !== typeof undefined && attr ? parseInt(attr) : 200;

        const postMessageDiv = postMessage?.querySelector<HTMLDivElement>('div');

        /* Affects template version 2 */
        messageWithoutLinks = this.removeAndRenderHashtags(postElement, messageWithoutLinks);

        if (updatedTemplate) {
            //if updatedTemplate.length is 1 then it uses template version 2
            const fontSize = this.getFontSize(postElement);
            postElement.style.fontSize = `${fontSize}px`;
        }

        if (messageWithoutLinks !== null && messageWithoutLinks != '') {
            if (updatedTemplate) {
                this.setPostMessage(
                    updatedTemplate,
                    postElement,
                    messageWithoutLinks,
                    maxChars,
                    uniqueId
                ); //for newer templates
            } else if (postMessageDiv) {
                postMessageDiv.innerHTML = truncateString(messageWithoutLinks || '', maxChars);
            }
        }

        if (postMessageDiv && postMessage) {
            // Shrink font size until message fits available space
            postMessageDiv.style.fontSize = '100px'; //reset font size

            while (postMessageDiv.clientHeight > postMessage.clientHeight) {
                postMessageDiv.style.fontSize = `${parseInt(postMessageDiv.style.fontSize) - 1}px`;
            }
        }

        if (this.#stacked !== true) {
            //do as normal
            document.querySelectorAll<HTMLDivElement>('.social-media-wall-post').forEach((post) => {
                post.style.left = '0px';
                post.style.visibility = 'visible';
            });

            if (index === totalPosts - 1) {
                const wrapper = document.createElement('div');
                wrapper.id = `social-media-wall-post-container-${uniqueId}`;
                wrapper.classList.add(
                    'social-media-wall-post-container',
                    'no-stacking',
                    'stacked-container-0',
                    'social-media-show-container'
                );
                wrap(postElement, wrapper);
            } else {
                const wrapper = document.createElement('div');
                wrapper.id = `social-media-wall-post-container-${uniqueId}`;
                wrapper.classList.add(
                    'social-media-wall-post-container',
                    'no-stacking',
                    'stacked-container-0',
                    'social-media-hide-container'
                );
                wrap(postElement, wrapper);
            }
        }
    }

    /**
     * Add hashtags to DOM
     */
    removeAndRenderHashtags(
        postElement: HTMLDivElement,
        message: string | null | undefined
    ): string | null | undefined {
        const hashtagDiv = postElement.querySelector<HTMLDivElement>('#hashtags');
        if (!hashtagDiv) {
            Log.Warning(`Could not find HTMLElement: #hashtags`, null, __fileline);
            return message;
        }

        // originally this removed the tags from facebook, but we found that people
        // use the hashtags in sentences (instead of at the end)
        hashtagDiv.style.display = 'none';

        return message;
    }

    /**
     * Starts the UI formatting of the posts
     */
    preloadPosts(): void {
        //Vue templates have custom javascript to format the post in the template
        //object itself so, skip this step
        if (this.template instanceof VueTemplate) {
            return;
        }

        this.#postArray.forEach((post, index, allPosts) => {
            this.loadPost(post, index, allPosts.length);
        });

        //if stacked do logic
        if (this.#stacked) {
            this.stackPosts();
        }
    }

    /**
     * Most likely stacking posts is not supported anymore
     * Manipulate DOM to group posts into stacked container div
     * @todo: remove jquery usage or remove function
     */
    stackPosts(): void {
        let containerTop = 0;
        let stackPage = 0;

        this.#stackPages.push({ id: stackPage, xtramedia: null });

        const jqWidgetWrapper = $(this.wrapperElement);

        const mediaWallPostContainerId = `social-media-wall-post-container-${this.id}-${stackPage}`;
        const stackedContainerHtml = `
            <div
                id="${mediaWallPostContainerId}"
                class="social-media-wall-post-container with-stacking stacked-container-${stackPage} social-media-hide-container"
            >
            </div>`;
        let stackedContainer = $(stackedContainerHtml);

        jqWidgetWrapper.append(stackedContainer);

        const mediaWallPostContainerSelector = `#${mediaWallPostContainerId}`;
        let containerHeight = $(mediaWallPostContainerSelector).outerHeight();

        jqWidgetWrapper.find(`.social-media-wall-post-${this.id}`).each((_idx, element) => {
            const jqElement = $(element);
            if (!jqElement) {
                throw new Error('Could not find HTMLElement.');
            }
            jqElement.css('left', '0px');
            jqElement.css('visibility', 'visible');

            const outerHeight = jqElement.outerHeight();
            if (outerHeight && containerHeight && outerHeight <= containerHeight) {
                jqElement.appendTo(stackedContainer);
                jqElement.css('top', `${containerTop}px`);

                containerHeight -= outerHeight;
                containerTop += outerHeight;
            } else {
                //need to create new container
                containerTop = 0;

                stackPage++;
                this.#stackPages.push({ id: stackPage, xtramedia: null });

                stackedContainer = $(stackedContainerHtml);

                jqWidgetWrapper.append(stackedContainer);

                containerHeight = $(mediaWallPostContainerSelector).outerHeight();
                jqElement.appendTo(mediaWallPostContainerSelector);
                jqElement.css('top', `${containerTop}px`);

                //@ts-ignore
                containerHeight -= jqElement.outerHeight();
                //@ts-ignore
                containerTop += jqElement.outerHeight();
            }
        });
    }

    protected getRenderContext(): SocialMediaRenderContext {
        const minFontSize = this.calculateOptimumFontSize(this.wrapperElement);
        const maxFontSize = this.getFontSize(this.wrapperElement);
        const actualMinFontSize = Math.min(maxFontSize, minFontSize);

        return {
            wid: this.id.toString(),
            wobj: {
                widget: {
                    widgetsid: this.id.toString(),
                    config: {
                        css: this.config.css ?? '',
                    },
                },
            },
            bodywidth: this.#width - 50,
            posts: this.#postArray.map((post) => {
                let shouldFontResize = false;
                if (isNullish(post.image) || post.image.length === 0) {
                    shouldFontResize = true;
                }

                return {
                    ...post,
                    fontResize: shouldFontResize,
                };
            }),
            Zone: {
                Id: this.zoneId,
            },
            zone: {
                id: this.zoneId,
            },
            Widget: {
                Id: this.id,
            },
            widget: {
                id: this.id,
            },
            medallion: {
                textOnly: this.#textOnly ? 'true' : 'false',
                picsOnly: this.#picsOnly ? 'true' : 'false',
            },
            metadata: {
                landscape: this.isLandscape(),
                fontResize: false,
                fontProperties: {
                    minSize: actualMinFontSize,
                    maxSize: maxFontSize,
                    maxTextHeight: this.isLandscape() ? (60 / 100) * this.#height : null,
                    ellipsis: true,
                },
            },
        };
    }

    /**
     * Render HTML template
     * @returns {Promise} Promise resolves when handlebars template has rendered
     */
    async renderTemplate(): Promise<void> {
        await this.renderContent();
        this.preloadPosts();
    }
}
